import { tokenManager } from "@ametie/vue-muza-use";
import { useToast } from "@app/core";
import type {
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteRecordNormalized,
  Router,
} from "vue-router";

import type { RouteMeta } from "../types/types";

import { useAuthStore } from "@/features/auth";
import { UserRole, type UserPermission } from "@/features/auth/types";
import { usePartnershipStore } from "@/features/partnership/store/partnershipStore";
import { PARTNERSHIP_BY_ALIAS, PARTNERSHIP_LABELS, PARTNERSHIPS, type Partnership, type PartnershipAlias } from "@/features/partnership/types";

// ============================================================================
// Router Instance Management
// ============================================================================

let routerInstance: Router | null = null;

/**
 * Set router instance for guards that need access to all routes
 * Must be called after router creation
 */
export function setRouterForGuards(router: Router): void {
  routerInstance = router;
}

// ============================================================================
// Permission Checking (Single Source of Truth)
// ============================================================================

/**
 * Check if current user has required permissions
 * @param required - Array of permission strings (all must match)
 * @param authStore - Optional auth store instance (uses singleton if not provided)
 * @returns true if user has all required permissions or is admin
 */
export function hasPermissions(
  required: readonly string[] | string[] | undefined,
  authStore?: ReturnType<typeof useAuthStore>,
): boolean {
  const store = authStore ?? useAuthStore();

  // No permissions required - allow access
  if (!required || required.length === 0) {
    return true;
  }

  // Not authenticated - deny access
  if (!store.isAuthenticated || !store.user) {
    return false;
  }

  // Admins bypass all permission checks
  if (store.user.role === UserRole.ADMIN) {
    return true;
  }

  // Check if user has ALL required permissions
  return required.every(permission =>
    store.hasPermission(permission as UserPermission),
  );
}

/**
 * Check if user has ANY of the required permissions (OR logic)
 * Used for parent menu items that should show if user has access to any child
 */
export function hasAnyPermission(
  required: readonly string[] | string[] | undefined,
  authStore?: ReturnType<typeof useAuthStore>,
): boolean {
  const store = authStore ?? useAuthStore();

  if (!required || required.length === 0) {
    return true;
  }

  if (!store.isAuthenticated || !store.user) {
    return false;
  }

  if (store.user.role === UserRole.ADMIN) {
    return true;
  }

  return required.some(permission =>
    store.hasPermission(permission as UserPermission),
  );
}

/**
 * Resolve which permission list applies for the active partnership.
 * Vendor Central with declared `vendorPermissions` → only that list is checked;
 * without it → falls back to `permissions` (shared pages keep working).
 * Seller Central always uses `permissions`.
 */
export const getEffectivePermissions = (
  meta: RouteMeta | undefined,
  partnership: Partnership,
): readonly string[] | string[] | undefined =>
  partnership === PARTNERSHIPS.VendorCentral && meta?.vendorPermissions
    ? meta.vendorPermissions
    : meta?.permissions;

// ============================================================================
// Partnership Checking (Single Source of Truth)
// ============================================================================

const SELLER_ONLY: readonly PartnershipAlias[] = ["seller"];

/**
 * Check if a route belongs to the active partnership.
 * Routes without meta.partnerships are seller-only by convention.
 * Declared on leaf routes only — vue-router merges matched-record meta,
 * so the guard always sees the leaf's own value (or absent → seller).
 */
export const routeMatchesPartnership = (
  metaPartnerships: readonly PartnershipAlias[] | undefined,
  active: Partnership,
): boolean =>
  (metaPartnerships ?? SELLER_ONLY).some(alias => PARTNERSHIP_BY_ALIAS[alias] === active);

// ============================================================================
// Route Finding Utilities
// ============================================================================

/**
 * Error/system routes that should never be auto-selected
 */
const EXCLUDED_ROUTES = new Set(["403", "404", "Forbidden", "NotFound", "Root"]);
const EXCLUDED_PATHS = new Set(["/", "/403", "/404", "/login", "/password-forgot", "/reset-password"]);

/**
 * Smart route finder with priority-based selection.
 * Finds the first accessible route for the current user in the active partnership.
 *
 * Priority order:
 * 1. Routes with explicit showInMenu: true
 * 2. Lower menuOrder values (1 < 2 < 3...)
 * 3. Shorter paths (top-level routes preferred)
 */
export function findFirstAccessibleRoute(
  routes: RouteRecordNormalized[],
  partnership?: Partnership,
): RouteLocationRaw | null {
  const partnershipStore = usePartnershipStore();
  const activePartnership = partnership ?? partnershipStore.partnership;
  const candidates: Array<{ route: RouteRecordNormalized, meta: RouteMeta }> = [];

  for (const route of routes) {
    const meta = route.meta as RouteMeta;
    const routeName = String(route.name ?? "");

    // Skip: public routes, unnamed, hidden, system routes, redirects, wildcards
    if (
      meta.requiresAuth === false
      || !route.name
      || meta.showInMenu === false
      || route.redirect
      || route.path.includes("*")
      || EXCLUDED_ROUTES.has(routeName)
      || EXCLUDED_PATHS.has(route.path)
    ) {
      continue;
    }

    // Check user access (partnership-effective permissions + target partnership)
    if (
      hasPermissions(getEffectivePermissions(meta, activePartnership))
      && routeMatchesPartnership(meta.partnerships, activePartnership)
    ) {
      candidates.push({ route, meta });
    }
  }

  if (candidates.length === 0) {
    return null;
  }

  // Sort by priority
  candidates.sort((a, b) => {
    // 1. Explicit menu items first
    const aInMenu = a.meta.showInMenu === true;
    const bInMenu = b.meta.showInMenu === true;
    if (aInMenu !== bInMenu) return aInMenu ? -1 : 1;

    // 2. By menuOrder (lower = higher priority)
    const aOrder = a.meta.menuOrder ?? 999;
    const bOrder = b.meta.menuOrder ?? 999;
    if (aOrder !== bOrder) return aOrder - bOrder;

    // 3. By path depth (shorter = higher priority)
    const aDepth = a.route.path.split("/").filter(Boolean).length;
    const bDepth = b.route.path.split("/").filter(Boolean).length;
    return aDepth - bDepth;
  });

  return { name: candidates[0].route.name as string };
}

// ============================================================================
// Auth Initialization Helper
// ============================================================================

/**
 * Initialize auth store if needed (has token but not initialized)
 * @returns true if initialization was successful or already initialized
 */
async function ensureAuthInitialized(authStore: ReturnType<typeof useAuthStore>): Promise<boolean> {
  if (authStore.isInitialized) {
    return true;
  }

  const token = tokenManager.getAccessToken();
  if (!token) {
    return false;
  }

  try {
    await authStore.initialize();
    return true;
  }
  catch (error) {
    console.error("[Auth Guard] Initialization failed:", error);
    // Clear invalid token
    tokenManager.clearTokens();
    return false;
  }
}

// ============================================================================
// Navigation Guards
// ============================================================================

/**
 * Main authentication guard
 * Handles: auth check, permission check, root redirect, error pages
 */
export async function authGuard(
  to: RouteLocationNormalized,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _from: RouteLocationNormalized,
): Promise<RouteLocationRaw | boolean | undefined> {
  const meta = to.meta as RouteMeta;
  const authStore = useAuthStore();
  const requiresAuth = meta.requiresAuth !== false;

  // Public routes - allow immediately
  if (!requiresAuth) {
    return true;
  }

  // Ensure auth is initialized
  await ensureAuthInitialized(authStore);

  // Not authenticated - redirect to login
  if (!authStore.isAuthenticated) {
    const loginPath = meta.authRedirect || "/login";

    // Avoid infinite redirect loop
    if (to.path === loginPath) {
      return true;
    }

    return {
      path: loginPath,
      // Carry the target so login can return here. Skipped for the root path —
      // the param would be meaningless there and only clutters the URL.
      query: to.fullPath === "/" ? undefined : { redirect: to.fullPath },
      replace: true,
    };
  }

  // Partnership state comes from the /me payload — hydrate once per session.
  // Must happen before any partnership-dependent logic (root redirect included).
  const partnershipStore = usePartnershipStore();
  if (authStore.user) {
    partnershipStore.initFromUser(authStore.user);
  }

  // Handle root redirect (find first accessible route)
  if (meta.isRootRedirect && routerInstance) {
    const routes = routerInstance.getRoutes();
    const firstAccessible = findFirstAccessibleRoute(routes);

    if (firstAccessible) {
      return Object.assign({}, firstAccessible, { replace: true });
    }

    // Nothing accessible in the active partnership — try the user's others.
    // If one has an accessible route, switch to it and land there instead of a
    // dead-end 403. (Validated against the target partnership before switching,
    // so the redirected route passes the guard on re-navigation — no loop.)
    const previous = partnershipStore.partnership;
    for (const candidate of partnershipStore.partnerships) {
      if (candidate === previous) continue;

      const routeInOther = findFirstAccessibleRoute(routes, candidate);
      if (!routeInOther) continue;

      const switched = await partnershipStore.switchPartnership(candidate);
      if (!switched) continue;

      useToast().success(
        `No pages available in ${PARTNERSHIP_LABELS[previous]} — `
        + `switched to ${PARTNERSHIP_LABELS[candidate]}`,
      );
      return Object.assign({}, routeInOther, { replace: true });
    }

    // No accessible routes in any partnership - show 403
    return { path: "/403", replace: true };
  }

  // Check permissions for protected routes (vendorPermissions in Vendor Central)
  if (!hasPermissions(getEffectivePermissions(meta, partnershipStore.partnership))) {
    return {
      path: "/403",
      replace: true,
    };
  }

  // Check partnership for protected routes.
  // Wrong partnership is not a permission error — the user has access, just in
  // another mode: redirect to the first accessible route instead of 403.
  if (!routeMatchesPartnership(meta.partnerships, partnershipStore.partnership)) {
    const firstAccessible = routerInstance
      ? findFirstAccessibleRoute(routerInstance.getRoutes())
      : null;

    if (firstAccessible) {
      return Object.assign({}, firstAccessible, { replace: true });
    }

    return { path: "/403", replace: true };
  }

  // All checks passed
  return true;
}

/**
 * Guest-only guard (for login/register pages)
 * Redirects authenticated users away from guest-only pages
 */
export async function guestGuard(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _to: RouteLocationNormalized,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _from: RouteLocationNormalized,
): Promise<RouteLocationRaw | boolean | undefined> {
  const authStore = useAuthStore();

  // Try to initialize auth
  await ensureAuthInitialized(authStore);

  // Authenticated users should not access guest pages
  if (authStore.isAuthenticated) {
    return { path: "/", replace: true };
  }

  return true;
}
